import React from "react";
import { Download, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useCurrency, CURRENCIES } from "../context/CurrencyContext";

// Shared "Export to Excel" dropdown.
//
// onExport(currency) is called with one of: "ORIGINAL" | "UAH" | "USD" | "EUR".
// The caller is responsible for actually building rows + writing the CSV; this
// component only exposes the currency choice. When currency != "ORIGINAL", the
// caller should convert each row's amount with the rate snapshot at export
// time and emit a preamble like "All amounts converted to USD @ 41.5 UAH/USD".
//
// Helper `buildExportPreamble` builds that preamble string so all three pages
// (payments, other-expenses, team-expenses) word it identically.
export function buildExportPreamble(targetCurrency, rates, rateDate) {
  if (!targetCurrency || targetCurrency === "ORIGINAL") return "";
  const date = rateDate || new Date().toISOString().slice(0, 10);
  if (targetCurrency === "UAH") {
    return `All amounts converted to UAH @ rates: 1 USD = ${(rates?.USD ?? 0).toFixed(2)} UAH, 1 EUR = ${(rates?.EUR ?? 0).toFixed(2)} UAH (PrivatBank on ${date}).`;
  }
  if (targetCurrency === "USD") {
    const eurToUsd = rates?.USD ? (rates?.EUR / rates?.USD) : 0;
    return `All amounts converted to USD @ rates: 1 USD = ${(rates?.USD ?? 0).toFixed(2)} UAH, 1 EUR = ${eurToUsd.toFixed(4)} USD (PrivatBank on ${date}).`;
  }
  // EUR
  const usdToEur = rates?.EUR ? (rates?.USD / rates?.EUR) : 0;
  return `All amounts converted to EUR @ rates: 1 EUR = ${(rates?.EUR ?? 0).toFixed(2)} UAH, 1 USD = ${usdToEur.toFixed(4)} EUR (PrivatBank on ${date}).`;
}

export default function ExportCurrencyButton({
  onExport,
  disabled = false,
  label = "Export to Excel",
  testId = "export-csv-btn",
  className = "border-zinc-700 text-zinc-300",
}) {
  const { rates, rateDate } = useCurrency();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={className}
          data-testid={testId}
          disabled={disabled}
          title="Export filtered rows to CSV (Excel-compatible). Choose a currency to convert all amounts, or keep original currencies."
        >
          <Download className="h-4 w-4 mr-2" />
          {label}
          <ChevronDown className="h-3 w-3 ml-2 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800 text-zinc-100 w-64">
        <DropdownMenuLabel className="text-xs text-zinc-500 font-normal">
          Currency for exported amounts
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-zinc-800" />
        <DropdownMenuItem
          onClick={() => onExport("ORIGINAL")}
          className="cursor-pointer focus:bg-zinc-800"
        >
          <div className="flex flex-col">
            <span className="text-sm">Original currencies</span>
            <span className="text-[11px] text-zinc-500">Keep each row in its native UAH/USD/EUR</span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-zinc-800" />
        {CURRENCIES.map((cur) => {
          const symbol = cur === "USD" ? "$" : cur === "EUR" ? "€" : "₴";
          return (
            <DropdownMenuItem
              key={cur}
              onClick={() => onExport(cur)}
              className="cursor-pointer focus:bg-zinc-800"
            >
              <div className="flex flex-col">
                <span className="text-sm">All in {cur} ({symbol})</span>
                <span className="text-[11px] text-zinc-500">
                  Convert every amount to {cur} using portal exchange rates
                  {rateDate ? ` (${rateDate})` : ""}
                </span>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
